/** 小时阅读卡语言独立配置列表与完整编辑；复用既有配置表格及弹窗，不逐字段继承。 */
import { UpsertReadingPassLanguageConfigRequestSchema } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type AppResponse,
  type ReadingPassLanguageConfigResponse,
  appQueryKeys,
  useReadingPassLanguageConfigs,
} from '~/data/queries';
import { readingPassLanguageConfigsApi } from '~/hooks/use-apps';
import { fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Section } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import {
  ReadingPassReminderFields,
  formatReadingPassError,
  readingPassReminderIssue,
} from './ReadingPassReminderFields';

interface LanguageConfigDraft {
  language: string;
  isEnabled: boolean;
  unlockDays: string;
  reminderEnabled: boolean;
  reminderTime: string;
  reminderMaxCount: string;
}

export function ReadingPassLanguageConfigsPanel({
  app,
  canEdit,
  supported,
}: {
  app: AppResponse;
  canEdit: boolean;
  supported: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useReadingPassLanguageConfigs(app.id, supported);
  const rows = query.data ?? [];
  const [editor, setEditor] = useState<{ isNew: boolean; draft: LanguageConfigDraft } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const availableLanguages = app.allowed_languages.filter(
    (language) => !rows.some((row) => row.language === language),
  );
  const canWrite = canEdit && supported;
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: appQueryKeys.readingPassLanguages(app.id) });
  const save = useMutation({
    mutationFn: ({
      language,
      body,
    }: {
      language: string;
      body: ReturnType<typeof UpsertReadingPassLanguageConfigRequestSchema.parse>;
    }) => readingPassLanguageConfigsApi.upsert(app.id, language, body),
    onSuccess: async () => {
      setEditor(null);
      setError(null);
      toast('语言独立配置已保存', { tone: 'pos' });
      await invalidate();
    },
    onError: async (failure) => {
      setError(formatReadingPassError(failure));
      await invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (language: string) => readingPassLanguageConfigsApi.remove(app.id, language),
    onSuccess: async () => {
      setDeleteTarget(null);
      setError(null);
      toast('已删除语言配置，恢复使用 App 默认', { tone: 'pos' });
      await invalidate();
    },
    onError: async (failure) => {
      setDeleteTarget(null);
      setError(formatReadingPassError(failure));
      await invalidate();
    },
  });
  const busy = save.isPending || remove.isPending;
  const openEditor = (row?: ReadingPassLanguageConfigResponse) => {
    setError(null);
    setEditor({
      isNew: !row,
      draft: row
        ? {
            language: row.language,
            isEnabled: row.is_enabled,
            unlockDays: String(row.unlock_reading_day_threshold),
            reminderEnabled: row.is_free_trial_remind_enabled,
            reminderTime: row.free_trial_remind_time,
            reminderMaxCount: String(row.free_trial_remind_max_count),
          }
        : {
            language: availableLanguages[0] ?? '',
            isEnabled: app.is_reading_pass_enabled,
            unlockDays: String(app.reading_pass_unlock_reading_day_threshold),
            reminderEnabled: app.is_reading_pass_free_trial_remind_enabled,
            reminderTime: app.reading_pass_free_trial_remind_time,
            reminderMaxCount: String(app.reading_pass_free_trial_remind_max_count),
          },
    });
  };
  const update = (patch: Partial<LanguageConfigDraft>) =>
    setEditor((current) =>
      current ? { ...current, draft: { ...current.draft, ...patch } } : current,
    );
  const submit = () => {
    if (!canWrite || busy || !editor) return;
    const draft = editor.draft;
    const reminderIssue = readingPassReminderIssue({
      enabled: draft.reminderEnabled,
      time: draft.reminderTime,
      maxCount: draft.reminderMaxCount,
    });
    if (reminderIssue) {
      setError(reminderIssue);
      return;
    }
    const parsed = UpsertReadingPassLanguageConfigRequestSchema.safeParse({
      is_enabled: draft.isEnabled,
      unlock_reading_day_threshold: Number(draft.unlockDays),
      is_free_trial_remind_enabled: draft.reminderEnabled,
      free_trial_remind_time: draft.reminderTime,
      free_trial_remind_max_count: Number(draft.reminderMaxCount),
    });
    if (!parsed.success) {
      setError('开启天数必须是不小于 1 的整数，且不超过 2147483647');
      return;
    }
    if (!draft.language || (editor.isNew && !availableLanguages.includes(draft.language))) {
      setError('请选择尚未配置的 App 允许语言');
      return;
    }
    setError(null);
    save.mutate({ language: draft.language, body: parsed.data });
  };
  const disabledTitle = !supported
    ? '服务端尚未支持阅读卡提醒与语言配置，部署完成后刷新即可使用'
    : !canEdit
      ? '仅 root / admin 可编辑；当前角色只读'
      : undefined;

  return (
    <Section
      title="阅读卡语言独立配置"
      sub="存在独立配置时五项整份覆盖 App 默认，不逐字段继承；删除后恢复 App 默认。免费体验卡时长始终由 App 统一配置。"
      action={
        <button
          type="button"
          className="btn btn-accent btn-sm"
          disabled={!canWrite || !query.isSuccess || availableLanguages.length === 0 || busy}
          title={
            disabledTitle ?? (availableLanguages.length === 0 ? '所有允许语言均已配置' : undefined)
          }
          onClick={() => openEditor()}
        >
          <Icons.IconPlus size={11} />
          新建语言配置
        </button>
      }
    >
      {!supported && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          {disabledTitle}
        </div>
      )}
      {supported && query.isPending && (
        <div style={{ padding: '14px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          语言配置加载中…
        </div>
      )}
      {query.isError && (
        <div
          role="alert"
          style={{
            padding: '14px 0',
            fontSize: 11.5,
            color: 'var(--neg)',
            overflowWrap: 'anywhere',
          }}
        >
          语言配置加载失败 · {formatReadingPassError(query.error)}{' '}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void query.refetch()}
          >
            重试
          </button>
        </div>
      )}
      {error && !editor && (
        <p role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
          {error}
        </p>
      )}
      {query.isSuccess && (
        <Table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>语言</th>
              <th>阅读卡</th>
              <th>开启天数</th>
              <th>领取提醒</th>
              <th>提醒时间</th>
              <th>累计次数上限</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.language}>
                <td>
                  <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
                    <span>{fmtLang(row.language)}</span>
                    {!app.allowed_languages.includes(row.language) && (
                      <span className="chip chip-warn">已移出允许语言</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`chip ${row.is_enabled ? 'chip-pos' : ''}`}>
                    {row.is_enabled ? '开启' : '关闭'}
                  </span>
                </td>
                <td className="mono">{row.unlock_reading_day_threshold}</td>
                <td>
                  <span className={`chip ${row.is_free_trial_remind_enabled ? 'chip-pos' : ''}`}>
                    {row.is_free_trial_remind_enabled ? '开启' : '关闭'}
                  </span>
                </td>
                <td className="mono">{row.free_trial_remind_time}</td>
                <td className="mono">{row.free_trial_remind_max_count}</td>
                <td>
                  <div className="hstack" style={{ gap: 6 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={!canWrite || busy}
                      title={disabledTitle}
                      aria-label={`编辑 ${row.language}`}
                      onClick={() => openEditor(row)}
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={!canWrite || busy}
                      title={disabledTitle}
                      aria-label={`删除 ${row.language}`}
                      onClick={() => {
                        setError(null);
                        setDeleteTarget(row.language);
                      }}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--fg-muted)' }}
                >
                  暂无独立配置 · 所有语言使用 App 默认
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      {editor && (
        <Dialog
          title={
            editor.isNew ? '新建阅读卡语言配置' : `编辑阅读卡语言配置 · ${editor.draft.language}`
          }
          onClose={() => {
            if (!busy) {
              setEditor(null);
              setError(null);
            }
          }}
          icon="IconBook"
          width={860}
          subtitle="五项配置完整保存；新建初值取已保存的 App 默认，之后独立维护。"
          footer={
            <>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => {
                  setEditor(null);
                  setError(null);
                }}
              >
                取消
              </button>
              <button
                type="button"
                className="btn btn-accent"
                disabled={!canWrite || busy}
                onClick={submit}
              >
                {save.isPending ? '保存中…' : '保存语言配置'}
              </button>
            </>
          }
        >
          <div style={{ padding: '0 18px' }}>
            <FormRow label="语言">
              {editor.isNew ? (
                <Select
                  value={editor.draft.language}
                  disabled={busy}
                  onChange={(event) => update({ language: event.target.value })}
                >
                  {availableLanguages.map((language) => (
                    <option key={language} value={language}>
                      {fmtLang(language)}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="chip">{fmtLang(editor.draft.language)}</span>
              )}
            </FormRow>
            {!app.allowed_languages.includes(editor.draft.language) && (
              <p className="chip chip-warn">此语言已移出 App 允许语言，历史配置仍可编辑和删除。</p>
            )}
            <FormRow label="小时阅读卡开关">
              <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
                <Switch
                  ariaLabel="语言小时阅读卡开关"
                  checked={editor.draft.isEnabled}
                  disabled={!canWrite || busy}
                  onChange={(isEnabled) => update({ isEnabled })}
                />
                <span className={`chip ${editor.draft.isEnabled ? 'chip-pos' : ''}`}>
                  {editor.draft.isEnabled ? '已开启' : '已关闭'}
                </span>
              </div>
            </FormRow>
            <FormRow label="开启天数（注册自然日龄）">
              <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input
                  aria-label="语言解锁天数"
                  className="input mono"
                  inputMode="numeric"
                  value={editor.draft.unlockDays}
                  disabled={!canWrite || busy}
                  onChange={(event) => update({ unlockDays: event.target.value })}
                  style={{ width: 100, maxWidth: '100%', textAlign: 'right' }}
                />
                <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>天</span>
              </div>
            </FormRow>
            <ReadingPassReminderFields
              draft={{
                enabled: editor.draft.reminderEnabled,
                time: editor.draft.reminderTime,
                maxCount: editor.draft.reminderMaxCount,
              }}
              disabled={!canWrite || busy}
              onChange={(patch) =>
                update({
                  ...(patch.enabled !== undefined ? { reminderEnabled: patch.enabled } : {}),
                  ...(patch.time !== undefined ? { reminderTime: patch.time } : {}),
                  ...(patch.maxCount !== undefined ? { reminderMaxCount: patch.maxCount } : {}),
                })
              }
            />
            {error && (
              <p role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
                {error}
              </p>
            )}
          </div>
        </Dialog>
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={`删除 ${deleteTarget} 的阅读卡语言配置？`}
          message="删除后该语言整份回到 App 默认；后续每日判定与提醒各阶段按当时配置生效。已领取体验卡、累计逻辑提醒次数和已有解锁终态不重置。"
          confirmLabel="删除并恢复默认"
          tone="danger"
          onClose={() => {
            if (!busy) setDeleteTarget(null);
          }}
          onConfirm={() => {
            if (canWrite && !busy) remove.mutate(deleteTarget);
          }}
        />
      )}
    </Section>
  );
}
